import React from "react";
import { Row, Col, Alert } from "reactstrap";
import NeoLoader from "components/Common/NeoLoader";
import useManagerDashboard from "../Manager/hooks/useManagerDashboard";
import StatCardAdvance from "../components/StatCardAdvance";
import RevenueExpenseChart from "./components/charts/RevenueExpenseChart";
import GrossSalesChart from "./components/charts/GrossSalesChart";
import DatePickerPopover from "../Manager/components/DatePickerPopover";

const FinancialDashboard = () => {
    const { loading, error, data, selectedDate, setSelectedDate } = useManagerDashboard();

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <NeoLoader type="inline" loading={loading} />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="danger">
                <i className="mdi mdi-alert-circle-outline me-2"></i>
                Erro ao carregar dados: {error}
            </Alert>
        );
    }

    // Ticket Médio: MRR / pagantes OU fallback para Vendas Brutas / Nº vendas
    const ticketMedio = data.ticketMedio > 0
        ? data.ticketMedio
        : (data.salesMonthCount > 0 ? (data.salesMonthGross / data.salesMonthCount) : 0);

    const previousTicketMedio = data.previousTicketMedio > 0
        ? data.previousTicketMedio
        : (data.previousSalesMonthCount > 0 ? (data.previousSalesMonthGross / data.previousSalesMonthCount) : 0);

    // Label dinâmico do dia selecionado
    const todayStr = new Date().toISOString().split('T')[0];
    const isToday = selectedDate === todayStr;
    const dayLabel = new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

    // Labels dinâmicos para período acumulado
    const periodLabel = isToday ? "no Mês" : `até ${dayLabel}`;
    const periodLabelBruto = isToday ? "no Mês (Bruto)" : `até ${dayLabel} (Bruto)`;
    const compLabel = isToday ? "M. anterior" : "Mesmo período (mês ant.)";

    return (
        <React.Fragment>
            <div className="d-flex justify-content-between align-items-center flex-wrap pt-2 mb-4 gap-3">
                {/* Lado esquerdo - Saudação outonal */}
                <div>
                    <h4 className="m-0" style={{ fontFamily: "'Outfit', 'Inter', sans-serif", fontWeight: "900", color: "#482712", letterSpacing: "-0.5px" }}>
                        Painel Financeiro 💰
                    </h4>
                    <p className="m-0" style={{ fontSize: "13px", fontWeight: "500", color: "#784b22" }}>
                        Acompanhe suas vendas, receitas e despesas.
                    </p>
                </div>

                {/* Lado direito - Data e Seletor */}
                <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                        <h5 className="m-0" style={{ fontFamily: "'Outfit', 'Inter', sans-serif", fontWeight: "900", color: "#4a2c11", letterSpacing: "0.5px" }}>
                            {new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </h5>
                    </div>
                    <DatePickerPopover selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
                </div>
            </div>

            {/* ── Cards Unificados ── */}
            <Row className="align-items-stretch">
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title={`Vendas do Dia (${dayLabel})`}
                        currentValue={data.salesTodayGross || 0}
                        subtitle={`${data.salesTodayCount || 0} vendas realizadas`}
                        icon="mdi mdi-cart-outline"
                        isCurrency={true}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title={`Vendas ${periodLabelBruto}`}
                        currentValue={data.salesMonthGross || 0}
                        previousValue={data.previousSalesMonthGross || 0}
                        goalValue={data.goals?.revenue}
                        subtitle={`${data.salesMonthCount || 0} vendas${data.goals?.salesCount ? ` / meta: ${data.goals.salesCount}` : ''}`}
                        icon="mdi mdi-cart-check"
                        isCurrency={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title="Ticket Médio"
                        currentValue={ticketMedio}
                        previousValue={previousTicketMedio}
                        goalValue={data.goals?.ticketMedio}
                        subtitle={data.ticketMedio > 0 ? "MRR / alunos pagantes" : `Média por venda ${periodLabel.toLowerCase()} (${data.salesMonthCount || 0} vendas)`}
                        icon="mdi mdi-ticket-percent-outline"
                        isCurrency={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title={`A Receber ${periodLabel}`}
                        currentValue={data.pendingIncomeMonthNet || 0}
                        previousValue={data.previousPendingIncomeMonthNet || 0}
                        subtitle={`Líquido previsto ${periodLabel.toLowerCase()}`}
                        icon="mdi mdi-calendar-clock"
                        isCurrency={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title={`Recebidos ${periodLabel}`}
                        currentValue={data.paidIncomeMonthNet || 0}
                        previousValue={data.previousPaidIncomeMonthNet || 0}
                        subtitle="Líquido já na conta"
                        icon="mdi mdi-cash-check"
                        isCurrency={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
                <Col lg={2} md={4} sm={6} className="mb-3">
                    <StatCardAdvance
                        title={`Despesas ${periodLabel}`}
                        currentValue={data.paidExpenseMonth || 0}
                        previousValue={data.previousPaidExpenseMonth || 0}
                        subtitle={`Despesas pagas ${periodLabel.toLowerCase()}`}
                        icon="mdi mdi-cash-minus"
                        isCurrency={true}
                        comparisonLabel={compLabel}
                    />
                </Col>
            </Row>

            {/* ── Gráficos Analíticos ── */}
            <Row>
                <Col md={6} className="mb-4">
                    <GrossSalesChart data={data.salesChartData || []} />
                </Col>
                <Col md={6} className="mb-4">
                    <RevenueExpenseChart data={data.financialChartData || []} />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default FinancialDashboard;
